import { useEffect, useRef, useState } from "react";
import { Layout } from "../components/Layout";
import { Card } from "../components/Card";
import { Banner } from "../components/Banner";
import { ArchiveFilters, type ArchiveFilterState } from "../components/ArchiveFilters";
import { ArchiveRow, type ArchiveRowData } from "../components/ArchiveRow";
import { api } from "../lib/api";

interface DashboardClientResponse {
  client: { client_id: string };
  accounts: Array<{ account_id: string; display_name: string }>;
}

interface ArchiveResponse {
  archive: ArchiveRowData[];
  offset: number;
  limit: number;
}

function readFiltersFromURL(): ArchiveFilterState {
  const params = new URLSearchParams(window.location.search);
  const ea = params.get("ea");
  const cad = params.get("cadence");
  return {
    endAdvertisers: ea ? ea.split(",").filter(Boolean) : [],
    cadence: cad === "weekly" || cad === "monthly" || cad === "all" ? cad : "all",
    from: params.get("from"),
    to: params.get("to"),
  };
}

function writeFiltersToURL(s: ArchiveFilterState) {
  const url = new URL(window.location.href);
  url.searchParams.delete("ea");
  url.searchParams.delete("cadence");
  url.searchParams.delete("from");
  url.searchParams.delete("to");
  if (s.endAdvertisers.length > 0) url.searchParams.set("ea", s.endAdvertisers.join(","));
  if (s.cadence !== "all") url.searchParams.set("cadence", s.cadence);
  if (s.from) url.searchParams.set("from", s.from);
  if (s.to) url.searchParams.set("to", s.to);
  window.history.replaceState(null, "", url.toString());
}

function buildQuery(s: ArchiveFilterState, offset: number, limit: number): string {
  const params = new URLSearchParams();
  if (s.endAdvertisers.length > 0) params.set("end_advertiser", s.endAdvertisers.join(","));
  if (s.cadence !== "all") params.set("cadence", s.cadence);
  if (s.from) params.set("from", s.from);
  if (s.to) params.set("to", s.to);
  params.set("offset", String(offset));
  params.set("limit", String(limit));
  return params.toString();
}

const PAGE_SIZE = 25;

export function ArchivePage() {
  const [filters, setFilters] = useState<ArchiveFilterState>(readFiltersFromURL);
  const [rows, setRows] = useState<ArchiveRowData[]>([]);
  const [accounts, setAccounts] = useState<DashboardClientResponse["accounts"]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api<DashboardClientResponse>("/api/dashboard")
      .then((d) => setAccounts(d.accounts))
      .catch(() => { /* dashboard is optional context; archive still works */ });
  }, []);

  useEffect(() => {
    writeFiltersToURL(filters);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setRows([]);
      setHasMore(true);
      setErr(null);
      const qs = buildQuery(filters, 0, PAGE_SIZE);
      api<ArchiveResponse>(`/api/archive?${qs}`)
        .then((r) => {
          setRows(r.archive);
          setHasMore(r.archive.length >= PAGE_SIZE);
        })
        .catch((e) => setErr(e.message ?? "Failed to load"));
    }, 300);
  }, [filters]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadingMore, hasMore, rows.length]);

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const qs = buildQuery(filters, rows.length, PAGE_SIZE);
      const r = await api<ArchiveResponse>(`/api/archive?${qs}`);
      setRows((prev) => [...prev, ...r.archive]);
      if (r.archive.length < PAGE_SIZE) setHasMore(false);
    } catch (e) {
      setErr((e as Error).message ?? "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleRetry(draftId: string) {
    try {
      await api(`/api/queue/draft/${encodeURIComponent(draftId)}/approve`, { method: "POST" });
      setFilters({ ...filters });
    } catch (e) {
      setErr((e as Error).message ?? "Retry failed");
    }
  }

  const isEmpty = rows.length === 0 && !err;

  return (
    <Layout>
      <div style={{ marginBottom: "var(--space-5)" }}>
        <span className="label">Reports</span>
        <h1 style={{ marginTop: 6 }}>Archive</h1>
      </div>

      {err && <Banner tone="err">{err}</Banner>}

      <Card padding="none">
        <div style={{ padding: "var(--space-3)" }}>
          <ArchiveFilters
            accounts={accounts}
            value={filters}
            onChange={setFilters}
          />

          {isEmpty && (
            <p style={{ color: "var(--color-bone-dim)", padding: "var(--space-4) 0" }}>
              No reports yet. Approved drafts will appear here after rendering. <a href="/dashboard/queue">Go to the Queue</a>.
            </p>
          )}

          <div role="table">
            {rows.map((row) => (
              <ArchiveRow key={row.render_id} row={row} onRetry={handleRetry} />
            ))}
          </div>

          {hasMore && rows.length > 0 && (
            <div ref={sentinelRef} style={{ padding: "var(--space-3)", textAlign: "center", color: "var(--color-bone-dim)", fontSize: "var(--fs-xs)" }}>
              {loadingMore ? "Loading more…" : ""}
            </div>
          )}
          {!hasMore && rows.length > 0 && (
            <div style={{ padding: "var(--space-3)", textAlign: "center", color: "var(--color-bone-dim)", fontSize: "var(--fs-xs)" }}>
              — end of archive —
            </div>
          )}
        </div>
      </Card>
    </Layout>
  );
}
